import { type ColumnDef } from '@tanstack/react-table';
import { StickyNote } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import type { OpportunityListItem } from '@/hooks/api/useOpportunities';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/formatters';
import { policyHasIncomeFeature, policyIncomeActivated } from '@/lib/income';
import { formatOpportunityStatus, getOpportunityStatusVariant } from '@/lib/opportunity-status';

// Widths for the right-pinned columns. DataTable sums these to place each pinned
// column, so they must be explicit — see its pinnedRightColumns prop.
const STATUS_COLUMN_WIDTH = 116;
const NOTES_COLUMN_WIDTH = 72;

function getScoreVariant(score: number) {
  if (score >= 80) return 'success' as const;
  if (score >= 50) return 'warning' as const;
  return 'secondary' as const;
}

export function getOpportunityColumns(
  options: {
    /**
     * The "Sent to Agent Platform" (actioned) view. When true, the table shows
     * the client name and policy number columns plus the forwarding details, and
     * drops the Status column (every row shares the same status here).
     */
    sentToAgentPlatform?: boolean;
  } = {}
): ColumnDef<OpportunityListItem, unknown>[] {
  const { sentToAgentPlatform = false } = options;
  const clientColumns: ColumnDef<OpportunityListItem, unknown>[] = sentToAgentPlatform
    ? [
        {
          accessorKey: 'clientName',
          header: 'Client',
          cell: ({ row }) => {
            const clientName = row.original.clientName;
            if (!clientName) return <span className="text-muted-foreground">—</span>;
            return <TruncatedTooltip text={clientName} className="text-sm font-medium" />;
          },
        },
        {
          accessorKey: 'policyNumber',
          header: 'Policy Number',
          cell: ({ row }) => {
            const policyNumber = row.original.policyNumber;
            if (!policyNumber) return <span className="text-muted-foreground">—</span>;
            return <span className="font-tabular text-sm">{policyNumber}</span>;
          },
        },
      ]
    : [];

  const columns: ColumnDef<OpportunityListItem, unknown>[] = [
    {
      accessorKey: 'propensityScore',
      header: 'Score',
      cell: ({ row }) => {
        const score = row.original.propensityScore;
        return (
          <Badge variant={getScoreVariant(score)} className="font-tabular">
            {Math.round(score)}
          </Badge>
        );
      },
    },
    ...clientColumns,
    {
      accessorKey: 'carrierName',
      header: 'Carrier / Product',
      cell: ({ row }) => (
        <div className="min-w-0">
          <TruncatedTooltip text={row.original.carrierName} className="font-medium" />
          <TruncatedTooltip
            text={[
              row.original.productName || row.original.productType,
              row.original.productName ? row.original.productType : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            className="text-xs text-muted-foreground"
          />
        </div>
      ),
    },
    {
      accessorKey: 'accountValue',
      header: 'Account Value',
      cell: ({ row }) => (
        <span className="font-tabular">{formatCurrency(row.original.accountValue)}</span>
      ),
    },
    {
      accessorKey: 'surrenderLossPercentage',
      header: 'Surr. Loss',
      cell: ({ row }) => {
        const loss = row.original.surrenderLossPercentage;
        if (loss == null) return <span className="text-muted-foreground">—</span>;
        return <span className="font-tabular">{`${loss.toFixed(1)}%`}</span>;
      },
    },
    {
      accessorKey: 'estimatedValue',
      header: 'Est. Value',
      cell: ({ row }) => (
        <span className="font-tabular">{formatCurrency(row.original.estimatedValue)}</span>
      ),
    },
    {
      accessorKey: 'agentName',
      header: 'Advisor',
      cell: ({ row }) => (
        <TruncatedTooltip
          text={row.original.agentName || 'Unknown'}
          className="text-sm font-medium"
        />
      ),
    },
    {
      accessorKey: 'ownerAge',
      header: 'Owner / Jurisdiction',
      cell: ({ row }) => {
        const age = row.original.ownerAge ?? row.original.clientAge;
        const jurisdiction = row.original.jurisdiction ?? row.original.clientState;
        if (!age && !jurisdiction) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="text-muted-foreground">
            {age ? `Age ${age}` : '—'}
            {jurisdiction ? `, ${jurisdiction}` : ''}
          </span>
        );
      },
    },
    {
      accessorKey: 'surrenderDate',
      header: 'Surrender',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{formatDate(row.original.surrenderDate)}</span>
      ),
    },
    {
      accessorKey: 'incomeBenefitValue',
      header: 'Income',
      cell: ({ row }) => {
        const o = row.original;
        if (!policyHasIncomeFeature(o)) {
          return <span className="text-muted-foreground">No</span>;
        }
        const activated = policyIncomeActivated(o);
        return (
          <div className="min-w-0">
            <div className="text-sm font-medium">{activated ? 'Active' : 'Not Started'}</div>
            {o.incomeBenefitValue != null && (
              <div className="font-tabular text-xs text-muted-foreground">
                {formatCurrency(o.incomeBenefitValue)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'salesTeam',
      header: 'Sales Team',
      cell: ({ row }) => {
        const team = row.original.salesTeam;
        if (!team) return <span className="text-muted-foreground">—</span>;
        return <TruncatedTooltip text={team} className="text-sm font-medium" />;
      },
    },
    {
      accessorKey: 'taxQualification',
      header: 'Tax Qualification',
      cell: ({ row }) => {
        const taxQualification = row.original.taxQualification;
        if (!taxQualification) return <span className="text-muted-foreground">—</span>;
        return <TruncatedTooltip text={taxQualification} className="text-sm" />;
      },
    },
    ...(sentToAgentPlatform
      ? [
          {
            accessorKey: 'forwardedAt',
            header: 'Date Sent',
            cell: ({ row }) => (
              <span className="whitespace-nowrap text-muted-foreground">
                {formatDateTime(row.original.forwardedAt)}
              </span>
            ),
          } satisfies ColumnDef<OpportunityListItem, unknown>,
          {
            accessorKey: 'forwardedBy',
            header: 'Forwarded By',
            cell: ({ row }) => (
              <TruncatedTooltip
                text={row.original.forwardedBy || 'Unknown'}
                className="text-sm font-medium"
              />
            ),
          } satisfies ColumnDef<OpportunityListItem, unknown>,
        ]
      : []),
    // Status is redundant in the "Sent to Agent Platform" view (every row is actioned).
    ...(sentToAgentPlatform
      ? []
      : [
          {
            id: 'status',
            accessorKey: 'status',
            header: 'Status',
            // Pinned to the right edge — an explicit size is required so the
            // Notes column beside it can compute its offset.
            size: STATUS_COLUMN_WIDTH,
            cell: ({ row }) => (
              <Badge variant={getOpportunityStatusVariant(row.original.status)}>
                {formatOpportunityStatus(row.original.status)}
              </Badge>
            ),
          } satisfies ColumnDef<OpportunityListItem, unknown>,
        ]),
    {
      id: 'notes',
      accessorKey: 'notes',
      header: 'Notes',
      size: NOTES_COLUMN_WIDTH,
      // This sorts on note presence (has_notes), so the useful first click is
      // the one that groups noted rows on top — descending. Left unset,
      // TanStack derives the first direction from the top row's value: notes is
      // a string on noted rows and null otherwise, so a page whose first row
      // happened to have a note would sort ascending and bury every noted row
      // on the last page.
      sortDescFirst: true,
      cell: ({ row }) => {
        const notes = row.original.notes;
        // The indicator exists only when there is something to indicate — an
        // empty cell is the signal that this opportunity has no note.
        if (!notes) return null;
        return (
          <TruncatedTooltip text={notes}>
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors bg-[hsl(var(--warning)/0.15)] border-[hsl(var(--warning)/0.35)] text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning)/0.25)]"
              aria-label="This opportunity has a note"
            >
              <StickyNote className="h-3.5 w-3.5" fill="currentColor" fillOpacity={0.25} />
            </span>
          </TruncatedTooltip>
        );
      },
    },
  ];
  return columns;
}

/**
 * Column ids frozen against the right edge of the opportunity table, so the Notes
 * indicator is reachable without scrolling a wide table. Status is dropped in the
 * Sent-to-Agent-Platform view, so it isn't pinned there.
 */
export function getPinnedRightColumns(sentToAgentPlatform = false): string[] {
  return sentToAgentPlatform ? ['notes'] : ['status', 'notes'];
}

export const opportunityColumns = getOpportunityColumns();
